import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import constants from '../lib/constants';
import { projectDraft } from '../lib/draftProjection';
import Loader from '../lib/components/Loader';
import ProjectedPlayer from '../lib/components/ProjectedPlayer';
import api from '../utils/api';
import '../Standings/Standings.css';

const PICKS_SHOWN = 12;

// The Drafts page lists the next two drafts; so does the home page.
const UPCOMING_DRAFT_YEARS = [constants.currentSeason + 1, constants.currentSeason + 2];

/**
 * One upcoming draft: the estimated franchise tags, then round 1 with the
 * manager on the clock (the new owner for a traded pick) and the best
 * remaining player projected to each pick. Everything comes from the player
 * list already loaded in the app, so it works before the draft table exists.
 */
const DraftYear = ({ year, playerData, outgoingPicks }) => {
    const [order, updateOrder] = useState(null);
    const [isProjected, updateIsProjected] = useState(true);
    const [loading, updateLoading] = useState(true);

    useEffect(() => {
        api.get(`/api/v1/draft/${year}/order`)
            .then(({ data }) => {
                updateOrder(data.order);
                updateIsProjected(data.isProjected);
            })
            .catch(() => updateOrder(null))
            .finally(() => updateLoading(false));
    }, [year]);

    if (loading) {
        return (
            <div className="mb-3">
                <h6 className="mb-2">{year} Draft - Round 1</h6>
                <Loader />
            </div>
        );
    }
    if (!order) {
        return null;
    }

    const { tags, picks } = projectDraft({
        playerData,
        year,
        currentSeason: constants.currentSeason,
        order: order.slice(0, PICKS_SHOWN),
        outgoingPicks: outgoingPicks || {},
    });

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-baseline mb-2">
                <h6 className="mb-0">{year} Draft - Round 1</h6>
                {isProjected && <span className="text-light opacity-75 small">Projected order</span>}
            </div>
            {tags.length > 0 && (
                <div className="mb-2">
                    <button
                        className="btn btn-sm btn-outline-warning"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#home-tags-${year}`}
                        aria-expanded="false"
                        aria-controls={`home-tags-${year}`}
                    >
                        <i className="bi bi-chevron-down me-1"></i>
                        Est. franchise tags ({tags.length})
                    </button>
                    <div className="collapse" id={`home-tags-${year}`}>
                        <ul className="list-unstyled small mt-2 mb-0">
                            {tags.map(({ manager, player }) => (
                                <li key={manager}>
                                    <strong>{manager}:</strong>{' '}
                                    <Link to={`/player/${player.id}`}>{player.name}</Link>{' '}
                                    <span className="text-light opacity-75">{player.posname}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            <div className="table-responsive">
                <table className="table table-sm table-dark table-striped standings-table align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Pick</th>
                            <th>Manager</th>
                            <th>Projected player</th>
                        </tr>
                    </thead>
                    <tbody>
                        {picks.map(({ pick, manager, tradedTo, player }) => (
                            <tr key={pick}>
                                <td>1.{pick}</td>
                                <td>
                                    {manager}
                                    {tradedTo && <strong> ({tradedTo})</strong>}
                                </td>
                                <td><ProjectedPlayer player={player} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const DraftCard = ({ playerData, outgoingPicks }) => {
    // The player list loads with the app; show a spinner until it arrives.
    if (!playerData) {
        return (
            <div className="home-card">
                <h5 className="mb-3">Upcoming Drafts</h5>
                <Loader />
            </div>
        );
    }
    return (
        <div className="home-card">
            <h5 className="mb-3">Upcoming Drafts</h5>
            {UPCOMING_DRAFT_YEARS.map(year => (
                <DraftYear key={year} year={year} playerData={playerData} outgoingPicks={outgoingPicks} />
            ))}
            <NavLink to="/draft" className="small">
                Full draft page <i className="bi bi-chevron-right"></i>
            </NavLink>
        </div>
    );
};

export default DraftCard;
